import React from 'react';
import { Check, Star, Clock, Users } from 'lucide-react';
import { scrollToSection } from '../utils/navigation';

const Tarifs = () => {
  const formations = [
    {
      title: "Formation Pizzaïolo",
      subtitle: "Base",
      price: "1000",
      duration: "35 heures",
      participants: "3 élèves max",
      popular: false,
      features: [
        "Techniques de base de la pizza",
        "Pâte traditionnelle",
        "Garniture classique",
        "Cuisson au four",
        "Hygiène et sécurité",
        "Certificat de formation"
      ]
    },
    {
      title: "Expert Pizza Napolitaine",
      subtitle: "Recommandé",
      price: "1800",
      duration: "40 heures",
      participants: "6 élèves max",
      popular: true,
      features: [
        "Toutes les techniques de base",
        "Histoire et tradition napolitaine",
        "Sélection premium des ingrédients",
        "Maîtrise de la pâte napolitaine",
        "Façonnage traditionnel",
        "Cuisson haute température",
        "6 modules vidéo inclus",
        "Certification Expert",
        "Suivi post-formation"
      ]
    },
    {
      title: "CQP Pizzaïolo",
      subtitle: "Éligible CPF",
      price: "2490",
      duration: "70 heures",
      participants: "6 élèves max",
      popular: false,
      features: [
        "Formation complète Expert",
        "Certification CQP reconnue",
        "Éligible au CPF",
        "Accompagnement emploi",
        "Stage en entreprise",
        "Garantie emploi",
        "Modules complémentaires",
        "Suivi 6 mois"
      ]
    }
  ];

  const complementaires = [
    {
      title: "Hygiène Alimentaire HACCP",
      price: "350",
      duration: "14 heures",
      description: "Formation obligatoire pour la restauration commerciale"
    },
    {
      title: "Création d'Entreprise",
      price: "650",
      duration: "21 heures",
      description: "Accompagnement complet pour créer votre pizzeria"
    }
  ];

  const ateliers = [
    {
      title: "Atelier Pizza Particulier",
      price: "90",
      duration: "3 heures",
      description: "Découverte de la pizza napolitaine"
    },
    {
      title: "Atelier Pizza & Socca",
      price: "110",
      duration: "4 heures",
      description: "Pizza napolitaine + spécialité niçoise"
    }
  ];

  return (
    <section id="tarifs" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Nos Tarifs
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Des formations de qualité à des prix justes. Financement possible par CPF, Pôle Emploi, OPCO.
          </p>
        </div>

        {/* Formations Principales */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {formations.map((formation, index) => (
            <div key={index} className={`relative bg-white rounded-2xl shadow-lg overflow-hidden ${formation.popular ? 'ring-4 ring-yellow-400' : ''}`}>
              {formation.popular && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className="bg-yellow-400 text-red-900 px-6 py-2 rounded-full font-bold text-sm flex items-center gap-1">
                    <Star className="h-4 w-4 fill-current" />
                    POPULAIRE
                  </div>
                </div>
              )}
              
              <div className="p-8">
                {/* Header */}
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{formation.title}</h3>
                  <p className="text-gray-600 mb-4">{formation.subtitle}</p>
                  <div className="text-5xl font-bold text-red-600 mb-2">
                    {formation.price}€
                  </div>
                  
                  {/* Info */}
                  <div className="flex justify-center gap-4 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {formation.duration}
                    </div>
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {formation.participants}
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="space-y-3 mb-8">
                  {formation.features.map((feature, featureIndex) => (
                    <div key={featureIndex} className="flex items-start gap-3">
                      <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <button 
                  onClick={() => scrollToSection('inscription')}
                  className={`w-full py-4 rounded-lg font-semibold text-lg transition-colors ${
                    formation.popular 
                      ? 'bg-yellow-400 text-red-900 hover:bg-yellow-300' 
                      : 'bg-red-600 text-white hover:bg-red-700'
                  }`}
                >
                  S'inscrire
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Formations Complémentaires */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-gray-900 text-center mb-8">Formations Complémentaires</h3>
          <div className="grid md:grid-cols-2 gap-8">
            {complementaires.map((formation, index) => (
              <div key={index} className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{formation.title}</h4>
                    <p className="text-gray-600">{formation.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-red-600">{formation.price}€</div>
                    <div className="text-sm text-gray-600">{formation.duration}</div>
                  </div>
                </div>
                <button 
                  onClick={() => scrollToSection('inscription')}
                  className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
                >
                  S'inscrire
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Ateliers Particuliers */}
        <div className="bg-gradient-to-r from-yellow-400 to-orange-400 rounded-2xl p-8">
          <h3 className="text-3xl font-bold text-red-900 text-center mb-8">Ateliers pour Particuliers</h3>
          <div className="grid md:grid-cols-2 gap-6">
            {ateliers.map((atelier, index) => (
              <div key={index} className="bg-white/20 backdrop-blur-sm rounded-xl p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-red-900 mb-2">{atelier.title}</h4>
                    <p className="text-red-800">{atelier.description}</p>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-red-900">{atelier.price}€</div>
                    <div className="text-sm text-red-800">{atelier.duration}</div>
                  </div>
                </div>
                <button 
                  onClick={() => scrollToSection('contact')}
                  className="w-full bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
                >
                  Réserver
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Financement */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Financement Disponible</h3>
          <p className="text-gray-600 mb-6">
            Nos formations peuvent être financées par CPF, Pôle Emploi, OPCO (AKTO/FAFIH), AGEFICE, FIFPL
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold">CPF</span>
            <span className="bg-green-100 text-green-800 px-4 py-2 rounded-full font-semibold">Pôle Emploi</span>
            <span className="bg-purple-100 text-purple-800 px-4 py-2 rounded-full font-semibold">OPCO</span>
            <span className="bg-orange-100 text-orange-800 px-4 py-2 rounded-full font-semibold">AGEFICE</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Tarifs;